import React, { useMemo } from 'react';
import type { MonthlyBucket, RateScenario, BankCostConfig } from '../../types';

function fmt(n: number) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(n);
}

function fmtMonth(iso: string) {
  const [year, month] = iso.split('-');
  const d = new Date(Number(year), Number(month) - 1);
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short' });
}

interface Props {
  months: MonthlyBucket[];
  scenarios: RateScenario[];
  bankCost: BankCostConfig;
}

interface MonthResult {
  month: string;
  paymentRev: number;
  payoutRev: number;
  totalRev: number;
  totalBankCost: number;
  profit: number;
}

interface ScenarioResult {
  scenario: RateScenario;
  months: MonthResult[];
  totalRev: number;
  totalBankCost: number;
  totalProfit: number;
}

function computeScenario(
  months: MonthlyBucket[],
  sc: RateScenario,
  bank: BankCostConfig
): ScenarioResult {
  const monthResults = months.map((m) => {
    const paymentRev =
      m.payment.count * sc.paymentFixedFee +
      m.payment.totalAmount * (sc.paymentRatePercent / 100);
    const payoutRev =
      m.payout.count * sc.payoutFixedFee +
      m.payout.totalAmount * (sc.payoutRatePercent / 100);
    const totalRev = paymentRev + payoutRev;

    let totalBankCost = 0;
    if (bank.enabled) {
      totalBankCost =
        m.payment.count * bank.paymentFixedFee +
        m.payment.totalAmount * (bank.paymentRatePercent / 100) +
        m.payout.count * bank.payoutFixedFee +
        m.payout.totalAmount * (bank.payoutRatePercent / 100);
    }

    return {
      month: m.month,
      paymentRev,
      payoutRev,
      totalRev,
      totalBankCost,
      profit: totalRev - totalBankCost,
    };
  });

  return {
    scenario: sc,
    months: monthResults,
    totalRev: monthResults.reduce((s, r) => s + r.totalRev, 0),
    totalBankCost: monthResults.reduce((s, r) => s + r.totalBankCost, 0),
    totalProfit: monthResults.reduce((s, r) => s + r.profit, 0),
  };
}

const COLORS = [
  'bg-indigo-50 text-indigo-900',
  'bg-emerald-50 text-emerald-900',
  'bg-amber-50 text-amber-900',
  'bg-rose-50 text-rose-900',
  'bg-cyan-50 text-cyan-900',
];

export function ComparisonTable({ months, scenarios, bankCost }: Props) {
  const results = useMemo(
    () => scenarios.map((sc) => computeScenario(months, sc, bankCost)),
    [months, scenarios, bankCost]
  );

  if (results.length === 0) return null;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Revenue Comparison</h3>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {results.map((r, i) => (
          <div
            key={r.scenario.id}
            className={`rounded-lg p-3 sm:p-4 ${COLORS[i % COLORS.length]}`}
          >
            <h4 className="font-medium text-sm">{r.scenario.name}</h4>
            <p className="mt-1 text-xs opacity-75">
              Pay: ${r.scenario.paymentFixedFee}/txn + {r.scenario.paymentRatePercent}% | 
              Out: ${r.scenario.payoutFixedFee}/txn + {r.scenario.payoutRatePercent}%
            </p>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Total Revenue</span>
                <span className="font-semibold">{fmt(r.totalRev)}</span>
              </div>
              {bankCost.enabled && (
                <div className="flex justify-between opacity-75">
                  <span>Bank Costs</span>
                  <span>-{fmt(r.totalBankCost)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold border-t border-current/10 pt-1">
                <span>{bankCost.enabled ? 'Profit' : 'Revenue'}</span>
                <span>{fmt(bankCost.enabled ? r.totalProfit : r.totalRev)}</span>
              </div>
              <div className="text-xs opacity-60">
                Avg {fmt((bankCost.enabled ? r.totalProfit : r.totalRev) / months.length)}/mo
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Month-by-Month Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">Month</th>
              {results.map((r) => (
                <th
                  key={r.scenario.id}
                  className="px-2 sm:px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap"
                >
                  {r.scenario.name}
                  <br />
                  <span className="font-normal text-xs">
                    {bankCost.enabled ? 'Profit' : 'Revenue'}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {months.map((m, mi) => (
              <tr key={m.month} className="hover:bg-gray-50">
                <td className="px-2 sm:px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                  {fmtMonth(m.month)}
                </td>
                {results.map((r) => {
                  const val = bankCost.enabled
                    ? r.months[mi].profit
                    : r.months[mi].totalRev;
                  return (
                    <td
                      key={r.scenario.id}
                      className={`px-2 sm:px-3 py-2 text-right whitespace-nowrap ${
                        val < 0 ? 'text-red-600' : 'text-gray-900'
                      }`}
                    >
                      {fmt(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold">
            <tr>
              <td className="px-2 sm:px-3 py-2 whitespace-nowrap">Total</td>
              {results.map((r) => {
                const val = bankCost.enabled ? r.totalProfit : r.totalRev;
                return (
                  <td
                    key={r.scenario.id}
                    className={`px-2 sm:px-3 py-2 text-right whitespace-nowrap ${
                      val < 0 ? 'text-red-600' : 'text-gray-900'
                    }`}
                  >
                    {fmt(val)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
